import { useState, useCallback, useEffect, useRef } from "react";
import { BrandDetails } from "../types/add-brand-types";
import { useGrowthApi } from "../api/use-growth-api";
import { SocialPostsMonitoring } from "../types/growth";
import { useTimeUtil } from "./useTimeUtil";
import useBrandApi from "../api/use-add-brand-api";

export interface DraftRepliesPopupData {
  replies: { reply: string; reply_2: string };
  postUrl: string;
}

// interface SocialSignal {
//   id: string;
//   platform: "reddit" | "twitter" | "linkedIn";
//   platformIcon: string;
//   source: string;
//   timestamp: string;
//   title: string;
//   content: string;
//   intent: "High Intent" | "Discovery" | "Low Intent";
// }

// Cache to persist data across component unmounts
const dataCache = new Map<
  string,
  {
    socialSignals: SocialPostsMonitoring[];
    competitorReviews: SocialPostsMonitoring[];
    timestamp: number;
  }
>();

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

function parseCompetitors(value: string | string[] | undefined): string[] {
  if (Array.isArray(value)) return value;
  if (typeof value === "string" && value.trim()) {
    return value
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
  }
  return [];
}

export const useMonitor = (
  brandDetails: BrandDetails,
  refreshBrandDetails?: () => void
) => {
  const { getSocialListeningPosts, getCompetitorReviews, markAsReplied, generateDraftReply } =
    useGrowthApi();
  const { updateBrand } = useBrandApi();
  const { formatIso } = useTimeUtil();
  const hasFetchedRef = useRef(false);
  const [toast, setToast] = useState<{
    message: string;
    type: "success" | "error" | "info" | "warning";
    isVisible: boolean;
  } | null>(null);

  const showToast = useCallback(
    (message: string, type: "success" | "error" | "info" | "warning") => {
      setToast({ message, type, isVisible: true });
    },
    []
  );

  const [socialSignals, setSocialSignals] = useState<SocialPostsMonitoring[]>(
    []
  );
  const [isLoading, setIsLoading] = useState(true);

  const [competitorReviews, setCompetitorReviews] = useState<SocialPostsMonitoring[]>([]);

  const [draftRepliesPopup, setDraftRepliesPopup] =
    useState<DraftRepliesPopupData | null>(null);
  const [generatingDraftForSignalId, setGeneratingDraftForSignalId] = useState<
    string | null
  >(null);

  const handleDraftReply = useCallback(
    async (signal: SocialPostsMonitoring) => {
      setGeneratingDraftForSignalId(signal.id);
      try {
        const response = await generateDraftReply(
          signal.product_id,
          signal.data.content,
          "reply",
          signal.platform
        );
        if (
          response &&
          response.content &&
          typeof response.content.reply === "string" &&
          typeof response.content.reply_2 === "string"
        ) {
          setDraftRepliesPopup({
            replies: {
              reply: response.content.reply,
              reply_2: response.content.reply_2,
            },
            postUrl: signal.url || "",
          });
        } else {
          showToast(
            "Could not generate draft replies. Please try again.",
            "error"
          );
        }
      } catch {
        showToast(
          "Could not generate draft replies. Please try again.",
          "error"
        );
      } finally {
        setGeneratingDraftForSignalId(null);
      }
    },
    [generateDraftReply, showToast]
  );

  const closeDraftRepliesPopup = useCallback(() => {
    setDraftRepliesPopup(null);
  }, []);

  const copyDraftReplyToClipboard = useCallback(
    async (text: string) => {
      try {
        await navigator.clipboard.writeText(text);
        showToast("Copied to clipboard", "success");
      } catch {
        showToast("Failed to copy", "error");
      }
    },
    [showToast]
  );

  const openDraftPostUrl = useCallback(() => {
    if (draftRepliesPopup?.postUrl) {
      window.open(draftRepliesPopup.postUrl, "_blank", "noopener,noreferrer");
    }
  }, [draftRepliesPopup?.postUrl]);

  const handleToastClose = useCallback(() => {
    setToast(null);
  }, []);

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [selectedSignalId, setSelectedSignalId] = useState<string | null>(null);

  const handleMarkAsRepliedClick = useCallback((signalId: string) => {
    setSelectedSignalId(signalId);
    setShowConfirmDialog(true);
  }, []);

  const getSocialPosts = useCallback(
    async (force: boolean = false) => {
      const cacheKey = `monitor_${brandDetails.id}`;
      const cachedData = dataCache.get(cacheKey);
      const now = Date.now();

      if (!force && cachedData && now - cachedData.timestamp < CACHE_DURATION) {
        setSocialSignals(cachedData.socialSignals);
        setCompetitorReviews(cachedData.competitorReviews);
        setIsLoading(false);
        return;
      }

      if (!force && hasFetchedRef.current) return;
      hasFetchedRef.current = true;

      try {
        setIsLoading(true);
        const [socialPosts, reviews] = await Promise.all([
          getSocialListeningPosts(brandDetails.id),
          getCompetitorReviews(brandDetails.id),
        ]);
        setSocialSignals(socialPosts);
        setCompetitorReviews(reviews);

        dataCache.set(cacheKey, {
          socialSignals: socialPosts,
          competitorReviews: reviews,
          timestamp: Date.now(),
        });
      } catch (error) {
        console.error("Error fetching monitor data:", error);
      } finally {
        setIsLoading(false);
      }
    },
    [brandDetails.id, getSocialListeningPosts, getCompetitorReviews]
  );

  const handleConfirmMarkAsReplied = useCallback(async () => {
    if (selectedSignalId) {
      const success = await markAsReplied(selectedSignalId);
      setShowConfirmDialog(false);
      setSelectedSignalId(null);
      if (success) {
        // Force refresh to get updated status
        hasFetchedRef.current = false;
        await getSocialPosts(true);
        showToast("Marked as replied successfully", "success");
      } else {
        showToast("Failed to mark as replied. Please try again.", "error");
      }
    }
  }, [getSocialPosts, markAsReplied, selectedSignalId, showToast]);

  const handleCancelMarkAsReplied = useCallback(() => {
    setShowConfirmDialog(false);
    setSelectedSignalId(null);
  }, []);

  const [generatingOutreachForReviewId, setGeneratingOutreachForReviewId] = useState<string | null>(null);

  const handleDraftOutreach = useCallback(
    async (review: SocialPostsMonitoring) => {
      setGeneratingOutreachForReviewId(review.id);
      try {
        const response = await generateDraftReply(
          review.product_id,
          review.data.content || review.data.raw_content,
          "outreach",
          review.platform
        );
        if (
          response &&
          response.content &&
          typeof response.content.reply === "string" &&
          typeof response.content.reply_2 === "string"
        ) {
          setDraftRepliesPopup({
            replies: {
              reply: response.content.reply,
              reply_2: response.content.reply_2,
            },
            postUrl: review.url || "",
          });
        } else {
          showToast("Could not generate outreach draft. Please try again.", "error");
        }
      } catch {
        showToast("Could not generate outreach draft. Please try again.", "error");
      } finally {
        setGeneratingOutreachForReviewId(null);
      }
    },
    [generateDraftReply, showToast]
  );

  const [showMonitoringSettings, setShowMonitoringSettings] = useState(false);

  const handleManageKeywords = useCallback(() => {
    setShowMonitoringSettings(true);
  }, []);

  const handleCloseMonitoringSettings = useCallback(() => {
    setShowMonitoringSettings(false);
  }, []);

  const [showEditCompetitorsModal, setShowEditCompetitorsModal] = useState(false);
  const [editCompetitorsList, setEditCompetitorsList] = useState<string[]>([]);
  const [isSavingCompetitors, setIsSavingCompetitors] = useState(false);

  const handleEditCompetitors = useCallback(() => {
    setEditCompetitorsList(
      parseCompetitors(brandDetails.marketing_strategy?.competitors)
    );
    setShowEditCompetitorsModal(true);
  }, [brandDetails.marketing_strategy?.competitors]);

  const handleCloseEditCompetitorsModal = useCallback(() => {
    setShowEditCompetitorsModal(false);
  }, []);

  const handleSaveCompetitors = useCallback(async () => {
    if (!brandDetails?.id) return;
    setIsSavingCompetitors(true);
    try {
      const competitorsPayload = {
        competitors: editCompetitorsList.filter((c) => c.trim() !== "").join(","),
      };
      const response = await updateBrand(competitorsPayload, brandDetails.id);
      if (response) {
        showToast("Competitors updated successfully", "success");
        refreshBrandDetails?.();
        setShowEditCompetitorsModal(false);
      } else {
        showToast("Failed to update competitors. Please try again.", "error");
      }
    } catch {
      showToast("Failed to update competitors. Please try again.", "error");
    } finally {
      setIsSavingCompetitors(false);
    }
  }, [
    brandDetails?.id,
    editCompetitorsList,
    updateBrand,
    refreshBrandDetails,
    showToast,
  ]);

  useEffect(() => {
    getSocialPosts();
  }, [getSocialPosts]);

  return {
    socialSignals,
    competitorReviews,
    isLoading,
    showMonitoringSettings,
    showConfirmDialog,
    handleDraftReply,
    handleMarkAsRepliedClick,
    handleConfirmMarkAsReplied,
    handleCancelMarkAsReplied,
    handleDraftOutreach,
    handleManageKeywords,
    handleCloseMonitoringSettings,
    handleEditCompetitors,
    showEditCompetitorsModal,
    editCompetitorsList,
    setEditCompetitorsList,
    handleCloseEditCompetitorsModal,
    handleSaveCompetitors,
    isSavingCompetitors,
    formatIso,
    toast,
    handleToastClose,
    draftRepliesPopup,
    closeDraftRepliesPopup,
    copyDraftReplyToClipboard,
    openDraftPostUrl,
    generatingDraftForSignalId,
    generatingOutreachForReviewId,
  };
};
